import { Navigate, RouteObject } from 'react-router-dom'
import { LoginPage } from '@shared/components/pages/LoginPage'
import { RoleGuard, ServiceGuard } from '@shared/components/auth'
import type { MerchantService } from '@shared/types/merchant'
import * as Pages from './lazy'

/** Wrap an element so only bank users can access it */
function bankOnly(element: React.ReactNode) {
  return <RoleGuard allowedRoles={['bank']}>{element}</RoleGuard>
}

/** Wrap an element so only merchant users can access it */
function merchantOnly(element: React.ReactNode) {
  return <RoleGuard allowedRoles={['merchant']}>{element}</RoleGuard>
}

/** Wrap an element so it is only accessible when the merchant has the given service enabled */
function merchantService(service: MerchantService, element: React.ReactNode) {
  return <ServiceGuard requiredService={service}>{element}</ServiceGuard>
}

/**
 * Authentication Routes
 * Public routes for login/auth flow
 */
export const authRoutes: RouteObject[] = [
  {
    path: '/login',
    element: <LoginPage />,
  },
]

/**
 * Protected Routes with Dashboard Layout
 * Main application routes with sidebar navigation
 */
export const dashboardRoutes: RouteObject[] = [
  // Home
  { path: '/', element: <Pages.HomePage /> },

  // Core entity list pages
  { path: '/payments', element: <Pages.PaymentsListPage /> },
  { path: '/payments/needs-attention', element: <Pages.PaymentsNeedsAttentionPage /> },
  { path: '/settings/payments', element: <Pages.PaymentsSettingsPage /> },
  { path: '/settings/payments/:configId', element: <Pages.PaymentConfigDetailPage /> },
  { path: '/customers', element: <Pages.CustomersListPage /> },
  { path: '/merchants', element: <Pages.MerchantsApplicationsPage /> },
  { path: '/merchants/applications', element: <Pages.MerchantsApplicationsPage /> },
  { path: '/merchants/create', element: <Pages.CreateMerchantPage /> },
  { path: '/merchants/:merchantId', element: <Pages.MerchantDetailsPage /> },
  { path: '/disputes', element: <Pages.DisputesListPage /> },
  { path: '/refunds', element: <Pages.RefundsListPage /> },
  { path: '/products', element: <Pages.ProductsPage /> },
  { path: '/products/pricing-table', element: <Pages.ProductsPage /> },
  { path: '/invoices', element: merchantService('billing', <Pages.InvoicesListPage />) },
  { path: '/invoices/create', element: merchantService('billing', <Pages.CreateInvoicePage />) },
  { path: '/invoices/:id/edit', element: merchantService('billing', <Pages.InvoiceWizardPage />) },
  { path: '/quotes', element: merchantService('billing', <Pages.QuotesListPage />) },
  { path: '/quotes/create', element: merchantService('billing', <Pages.CreateQuotePage />) },
  { path: '/billing-schedules', element: merchantService('billing', <Pages.BillingSchedulesListPage />) },
  { path: '/settings/billing', element: merchantService('billing', <Pages.BillingSettingsPage />) },
  { path: '/billing/settings', element: <Navigate to="/settings/billing" replace /> },
  { path: '/subscriptions', element: merchantService('billing', <Pages.SubscriptionsPage />) },
  { path: '/subscriptions/plans', element: merchantService('billing', <Pages.SubscriptionsPage />) },

  // Financial and billing pages
  { path: '/balances', element: <Pages.CryptoBalancesPage /> },
  { path: '/balance', element: <Pages.BalancePage /> },
  { path: '/balance/activity', element: <Pages.BalanceActivityPage /> },
  { path: '/balance/invoices', element: <Pages.BalanceInvoicesPage /> },
  { path: '/balance/sales', element: <Pages.BalanceSalesPage /> },
  { path: '/balance/payouts', element: <Pages.BalancePayoutsPage /> },
  { path: '/balance/reserves', element: <Pages.BalanceReservesPage /> },
  { path: '/balance-hub', element: merchantOnly(<Pages.BalanceHubPage />) },
  { path: '/balance-hub/activity/:type', element: merchantOnly(<Pages.BalanceActivityDetailPage />) },
  { path: '/balance-hub/wallets/:walletId', element: merchantOnly(<Pages.WalletDetailPage />) },
  {
    path: '/billing',
    element: merchantService('billing', <Pages.BillingPage />),
    children: [
      { path: 'revenue', element: <Pages.BillingRevenueTab /> },
      { path: 'usage', element: <Pages.BillingUsageTab /> },
      { path: 'subscribers', element: <Pages.BillingSubscribersTab /> },
      { path: 'trials', element: <Pages.BillingTrialsTab /> },
      { path: 'churn', element: <Pages.BillingChurnTab /> },
      { path: 'collections', element: <Pages.BillingCollectionsTab /> },
    ],
  },
  { path: '/usage-billing', element: merchantService('billing', <Navigate to="/usage-billing/meters" replace />) },
  { path: '/usage-billing/events', element: merchantService('billing', <Pages.UsageBasedBillingPage />) },
  { path: '/usage-billing/meters', element: merchantService('billing', <Pages.UsageBasedBillingPage />) },
  { path: '/usage-billing/credits', element: merchantService('billing', <Pages.UsageBasedBillingPage />) },
  { path: '/revenue-recognition', element: merchantService('billing', <Pages.RevenueRecognitionPage />) },
  { path: '/revenue-recognition/rules/:ruleId', element: merchantService('billing', <Pages.RevRecRuleDetailPage />) },
  { path: '/settings/revenue-recognition', element: merchantService('billing', <Pages.RevRecSettingsPage />) },

  // Risk and engagement services (Sonar)
  { path: '/sonar', element: merchantService('sonar', <Pages.SonarPage />) },
  { path: '/sonar/lists/:listId', element: merchantService('sonar', <Pages.SonarListDetailPage />) },
  { path: '/sonar/rules/:ruleId', element: merchantService('sonar', <Pages.SonarRuleDetailPage />) },
  { path: '/sonar/vamp/:merchantId', element: merchantService('sonar', <Pages.VAMPPage />) },
  { path: '/sonar/schedules/:scheduleId', element: bankOnly(<Pages.FraudScheduleDetailPage />) },
  { path: '/sonar/velocity-limits/:limitId', element: bankOnly(<Navigate to="/sonar?tab=velocity-limits" replace />) },
  { path: '/settings/engagement', element: <Pages.EngagementSettingsPage /> },

  // Sonar 3 — redirect to main Sonar (bank users see BankSonarPage via role check)
  { path: '/sonar3', element: bankOnly(<Navigate to="/sonar" replace />) },

  { path: '/risk-scorecards', element: bankOnly(<Pages.RiskScorecardsPage />) },
  // Engagement
  { path: '/engagement', element: merchantService('engagement', <Pages.EngagementOverviewPage />) },
  { path: '/engagement/campaigns', element: merchantService('engagement', <Pages.CampaignsListPage />) },
  {
    path: '/engagement/campaigns/:id',
    element: merchantService('engagement', <Pages.CampaignDetailPage />),
    children: [
      { index: true, element: <Navigate to="overview" replace /> },
      { path: 'overview', element: <Pages.CampaignOverviewTab /> },
      { path: 'performance', element: <Pages.CampaignPerformanceTab /> },
      { path: 'enrollment', element: <Pages.CampaignEnrollmentTab /> },
      { path: 'configuration', element: <Pages.CampaignConfigurationTab /> },
      { path: 'activity', element: <Pages.CampaignActivityTab /> },
    ],
  },
  { path: '/engagement/enrollment', element: merchantService('engagement', <Pages.EnrollmentListPage />) },
  { path: '/engagement/enrollment/:id', element: merchantService('engagement', <Pages.EnrollmentDetailPage />) },
  // Rewards Catalog
  { path: '/engagement/rewards', element: merchantService('engagement', <Pages.RewardsCatalogListPage />) },
  {
    path: '/engagement/rewards/:id',
    element: merchantService('engagement', <Pages.RewardDetailPage />),
    children: [
      { index: true, element: <Navigate to="overview" replace /> },
      { path: 'overview', element: <Pages.RewardOverviewTab /> },
      { path: 'usage', element: <Pages.RewardUsageTab /> },
      { path: 'analytics', element: <Pages.RewardAnalyticsTab /> },
    ],
  },
  // Points Ledger
  { path: '/engagement/points', element: merchantService('engagement', <Pages.PointsLedgerListPage />) },
  { path: '/engagement/points/customer/:customerId', element: merchantService('engagement', <Pages.CustomerPointsDetailPage />) },
  // Tier Management
  { path: '/engagement/tiers', element: merchantService('engagement', <Pages.TierManagementPage />) },
  { path: '/engagement/tiers/:tierId', element: merchantService('engagement', <Pages.TierDetailPage />) },
  { path: '/engagement/analytics', element: merchantService('engagement', <Pages.EngagementAnalyticsPage />) },
  { path: '/engagement/competitive', element: merchantService('engagement', <Pages.EngagementCompetitivePage />) },

  // POS
  { path: '/pos', element: merchantService('pos', <Pages.POSPage />) },
  { path: '/pos/menus', element: merchantService('pos', <Pages.MenusPage />) },
  { path: '/pos/menus/:menuId', element: merchantService('pos', <Pages.MenuBuilderPage />) },
  { path: '/pos/recipes', element: merchantService('pos', <Pages.RecipesTabsPage />) },
  { path: '/pos/recipes/create', element: merchantService('pos', <Pages.RecipeCreatePage />) },
  { path: '/pos/recipes/:id', element: merchantService('pos', <Pages.RecipeDetailPage />) },
  { path: '/pos/schedule', element: merchantService('pos', <Pages.SchedulePage />) },
  { path: '/pos/inventory', element: merchantService('pos', <Pages.PosInventoryPage />) },
  { path: '/pos/inventory/:id', element: merchantService('pos', <Pages.PosInventoryDetailPage />) },
  { path: '/pos/recipe-costing', element: merchantService('pos', <Pages.RecipesTabsPage />) },
  { path: '/pos/purchase-orders', element: merchantService('pos', <Pages.PosInventoryPage />) },

  // Payments channel pages — Performance (tabbed: approvals, declines, chargebacks, disputes, refunds, alerts)
  { path: '/payments-channels/performance', element: merchantService('performance', <Pages.PaymentsPerformancePage />) },
  { path: '/payments-channels/approvals', element: merchantService('performance', <Pages.PaymentsPerformancePage />) },
  { path: '/payments-channels/declines', element: merchantService('performance', <Pages.PaymentsPerformancePage />) },
  { path: '/payments-channels/chargebacks', element: merchantService('performance', <Pages.PaymentsPerformancePage />) },
  { path: '/payments-channels/disputes', element: merchantService('performance', <Pages.PaymentsPerformancePage />) },
  { path: '/payments-channels/refunds', element: merchantService('performance', <Pages.PaymentsPerformancePage />) },
  { path: '/payments-channels/alerts', element: merchantService('performance', <Pages.PaymentsPerformancePage />) },
  // Separate pages
  { path: '/payments-channels/bins', element: merchantService('bins', <Pages.PaymentsBinsPage />) },
  { path: '/payments-channels/cost', element: merchantService('cost', <Pages.PaymentsCostPage />) },
  { path: '/payments-channels/cost/invoices', element: merchantService('cost', <Pages.PaymentsCostPage />) },
  { path: '/payments-channels/cost/effective-rate', element: merchantService('cost', <Pages.PaymentsCostPage />) },
  { path: '/payments-channels/cost/interchange', element: merchantService('cost', <Pages.PaymentsCostPage />) },
  { path: '/payments-channels/cost/penalties', element: merchantService('cost', <Pages.PaymentsCostPage />) },
  { path: '/payments-channels/opportunities', element: merchantService('opportunities', <Pages.PaymentsOpportunitiesPage />) },
  { path: '/payments-channels/opportunities/network-tokenization', element: merchantService('opportunities', <Pages.PaymentsOpportunitiesPage />) },
  { path: '/payments-channels/opportunities/debit-routing', element: merchantService('opportunities', <Pages.PaymentsOpportunitiesPage />) },
  // Tokens
  { path: '/payments-channels/tokens', element: merchantService('tokens', <Navigate to="/payments-channels/tokens/native-tokens" replace />) },
  { path: '/payments-channels/tokens/native-tokens', element: merchantService('tokens', <Pages.TokensPage />) },
  { path: '/payments-channels/tokens/network-tokens', element: merchantService('tokens', <Pages.TokensPage />) },
  { path: '/payments-channels/tokens/account-updater', element: merchantService('tokens', <Pages.TokensPage />) },
  // Legacy redirects for old routes
  { path: '/payments-channels/netw', element: <Navigate to="/payments-channels/tokens/network-tokens" replace /> },
  { path: '/payments-channels/uprdr', element: <Navigate to="/payments-channels/tokens/account-updater" replace /> },
  { path: '/payments-channels/anomalies', element: <Navigate to="/payments-channels/performance" replace /> },
  { path: '/payments-channels/market', element: <Navigate to="/payments-channels/performance" replace /> },
  { path: '/payments-channels/mrkt', element: <Navigate to="/payments-channels/performance" replace /> },

  // E-commerce and payment solutions
  { path: '/in-stores', element: merchantService('pos', <Navigate to="/in-stores/terminals" replace />) },
  { path: '/in-stores/terminals', element: merchantService('pos', <Pages.InStoresPage />) },
  { path: '/in-stores/softpos', element: merchantService('pos', <Pages.InStoresPage />) },
  { path: '/in-stores/inventory', element: merchantService('pos', <Pages.InStoresPage />) },
  { path: '/in-stores/orders', element: merchantService('pos', <Pages.InStoresPage />) },
  { path: '/ecommerce', element: merchantService('ecommerce', <Pages.EcommerceListPage />) },
  { path: '/checkout', element: merchantService('ecommerce', <Pages.CheckoutPage />) },
  { path: '/payment-links', element: merchantService('payment-links', <Pages.PaymentLinksListPage />) },
  { path: '/digital', element: <Pages.DigitalListPage /> },

  // Business tools and management (bank-only)
  { path: '/members', element: bankOnly(<Pages.MembersPage />) },
  { path: '/members/organizations', element: bankOnly(<Pages.MembersPage />) },
  { path: '/cover', element: <Pages.CoverListPage /> },

  // Banks - detail (bank-only)
  { path: '/members/banks/:id', element: bankOnly(<Pages.BankDetailPage />) },

  // Organizations - detail (bank-only)
  { path: '/members/organizations/:id', element: bankOnly(<Pages.OrganizationDetailPage />) },
  { path: '/programs', element: bankOnly(<Pages.ProgramsPage />) },
  { path: '/programs/products', element: bankOnly(<Pages.ProgramsPage />) },
  { path: '/programs/services', element: bankOnly(<Pages.ProgramsPage />) },
  { path: '/programs/:id', element: bankOnly(<Pages.CardProgramDetailPage />) },
  { path: '/programs/products/:id', element: bankOnly(<Pages.ProgramProductDetailPage />) },

  // Money Movement (bank-only)
  { path: '/money-movement/balance', element: bankOnly(<Pages.MMBalancePage />) },
  { path: '/money-movement/accounts', element: bankOnly(<Pages.MMAccountsPage />) },
  { path: '/money-movement/accounts/reserves', element: bankOnly(<Pages.MMAccountsPage />) },
  { path: '/money-movement/operations', element: bankOnly(<Pages.MMOperationsPage />) },
  { path: '/money-movement/operations/payouts', element: bankOnly(<Pages.MMOperationsPage />) },
  { path: '/money-movement/operations/adjustments', element: bankOnly(<Pages.MMOperationsPage />) },
  { path: '/money-movement/operations/deposits', element: bankOnly(<Pages.MMOperationsPage />) },
  { path: '/money-movement/operations/collected-fees', element: bankOnly(<Pages.MMOperationsPage />) },
  { path: '/money-movement/system-accounts', element: bankOnly(<Pages.MMSystemAccountsPage />) },
  { path: '/money-movement/accounts/reserves/:id', element: bankOnly(<Pages.MMReserveDetailPage />) },
  { path: '/money-movement/accounts/:id', element: bankOnly(<Pages.MMAccountDetailPage />) },
  { path: '/money-movement/operations/payouts/:id', element: bankOnly(<Pages.MMPayoutDetailPage />) },
  { path: '/money-movement/operations/deposits/:id', element: bankOnly(<Pages.MMDepositDetailPage />) },
  { path: '/money-movement/operations/collected-fees/:id', element: bankOnly(<Pages.MMCollectedFeeDetailPage />) },
  { path: '/money-movement/operations/adjustments/:id', element: bankOnly(<Pages.MMAdjustmentDetailPage />) },
  { path: '/money-movement/operations/:id', element: bankOnly(<Pages.MMOperationDetailPage />) },

  // Billing Admin (bank-only)
  { path: '/billing-admin', element: bankOnly(<Pages.BillingAdminPage />) },
  { path: '/billing-admin/fees', element: bankOnly(<Pages.BillingAdminPage />) },
  { path: '/billing-admin/fx-rates', element: bankOnly(<Pages.BillingAdminPage />) },
  { path: '/billing-admin/reserves', element: bankOnly(<Pages.BillingAdminPage />) },
  { path: '/billing-admin/payouts', element: bankOnly(<Pages.BillingAdminPage />) },
  { path: '/billing-admin/reserves/:id', element: bankOnly(<Pages.BillingReserveDetailPage />) },
  { path: '/billing-admin/payouts/:id', element: bankOnly(<Pages.BillingPayoutDetailPage />) },
  { path: '/billing-admin/tariffs/:id', element: bankOnly(<Pages.TariffDetailPage />) },
  { path: '/billing-admin/fees/:id', element: bankOnly(<Pages.FeeDetailPage />) },
  { path: '/billing-admin/fx-rates/:id', element: bankOnly(<Pages.FxRateDetailPage />) },

  // Rails section (bank-only)
  { path: '/rails', element: bankOnly(<Navigate to="/rails/overview" replace />) },
  { path: '/rails/overview', element: bankOnly(<Pages.RailsPage />) },
  { path: '/rails/overview/alerts', element: bankOnly(<Pages.RailsPage />) },
  { path: '/rails/overview/reviews', element: bankOnly(<Pages.RailsPage />) },
  { path: '/rails/card-rails', element: bankOnly(<Pages.RailsPage />) },
  { path: '/rails/card-rails/clearing', element: bankOnly(<Pages.RailsPage />) },
  { path: '/rails/card-rails/settlement', element: bankOnly(<Pages.RailsPage />) },
  { path: '/rails/card-rails/disputes', element: bankOnly(<Pages.RailsPage />) },
  { path: '/rails/card-rails/reconciliation', element: bankOnly(<Pages.RailsPage />) },
  // Legacy redirects
  { path: '/rails/clearing', element: bankOnly(<Navigate to="/rails/card-rails/clearing" replace />) },
  { path: '/rails/settlement', element: bankOnly(<Navigate to="/rails/card-rails/settlement" replace />) },
  { path: '/clearing', element: bankOnly(<Navigate to="/rails/card-rails/clearing" replace />) },
  { path: '/settlement', element: bankOnly(<Navigate to="/rails/card-rails/settlement" replace />) },
  { path: '/reconciliations', element: bankOnly(<Pages.ReconciliationsListPage />) },
  { path: '/velocity', element: bankOnly(<Navigate to="/sonar?tab=velocity-limits" replace />) },
  { path: '/velocity/:id', element: bankOnly(<Pages.VelocityLimitDetailPage />) },

  // Reporting
  { path: '/reporting', element: <Navigate to="/reporting/reports" replace /> },
  { path: '/reporting/reports', element: <Pages.ReportsPage /> },
  { path: '/reporting/pipelines', element: <Pages.PipelinesPage /> },
  { path: '/reporting/pipelines/connectors', element: <Pages.PipelinesPage /> },

  // Integrations
  { path: '/integrations', element: <Pages.IntegrationsPage /> },

  // Compliance section (bank-only)
  { path: '/compliance', element: bankOnly(<Navigate to="/compliance/konar" replace />) },
  { path: '/compliance/konar', element: bankOnly(<Pages.KonarPage />) },
  { path: '/compliance/konar/cases/:caseId', element: bankOnly(<Pages.ReviewCaseDetailPage />) },
  { path: '/compliance/konar/rules/:ruleId', element: bankOnly(<Pages.KonarRuleDetailPage />) },
  { path: '/compliance/konar/lists/:listId', element: bankOnly(<Pages.KonarListDetailPage />) },
  { path: '/compliance/scorecards', element: bankOnly(<Pages.ScorecardsPage />) },
  { path: '/compliance/scorecards/:id', element: bankOnly(<Pages.ScorecardDetailPage />) },
  { path: '/compliance/verifications', element: bankOnly(<Pages.VerificationsPage />) },
  { path: '/compliance/verifications/businesses/:id', element: bankOnly(<Pages.BusinessVerificationDetailPage />) },
  { path: '/compliance/verifications/individuals/:id', element: bankOnly(<Pages.IndividualVerificationDetailPage />) },
  { path: '/compliance/sar', element: bankOnly(<Pages.SARPage />) },
  { path: '/compliance/sar/:caseId', element: bankOnly(<Pages.SARCaseDetailPage />) },

  // Bookings
  { path: '/bookings', element: <Pages.CustomerBookingPortal /> },
  { path: '/bookings/reminders', element: <Pages.BookingReminders /> },
  { path: '/bookings/:id', element: <Pages.BookingDetailView /> },

  // Dev tools
  { path: '/dev/pages', element: <Pages.ComponentDemoPage /> },
  { path: '/dev/dialogs', element: <Pages.DialogsComponentsPage /> },
  { path: '/dev/components', element: <Pages.ComponentLibraryPage /> },
  { path: '/dev/transaction-components', element: <Pages.TransactionComponentLibraryPage /> },
  { path: '/dev/test', element: <Pages.TestPage /> },
  { path: '/dev/opportunities-components', element: <Pages.OpportunitiesComponentsPage /> },
  { path: '/dev/payments-cost-components', element: <Pages.PaymentsCostComponentsPage /> },
  { path: '/dev/sheets', element: <Pages.SheetsComponentsPage /> },
  { path: '/dev/rules', element: <Pages.RulesComponentsPage /> },
  { path: '/dev/rule-builder', element: <Pages.RuleCreateWizardPage /> },
  { path: '/dev/risk-scores', element: <Pages.RiskScoresPage /> },

  // POS Settings
  { path: '/settings/pos', element: merchantService('pos', <Pages.POSSettingsPage />) },
  { path: '/settings/stores', element: merchantService('pos', <Pages.StoresListPage />) },
  { path: '/settings/stores/:storeId', element: merchantService('pos', <Pages.StoreDetailPage />) },
  { path: '/settings/tax', element: <Pages.TaxSettingsPage /> },

  // Settings (role-aware)
  { path: '/settings', element: <Pages.SettingsRouter /> },
  { path: '/settings/personal', element: <Pages.PersonalDetailsPage /> },
  { path: '/settings/balance', element: <Pages.BalanceSettingsPage /> },
  { path: '/settings/team-security', element: <Pages.TeamSecurityPage /> },
  { path: '/settings/sonar', element: <Pages.SonarSettingsPage /> },

  // Entity detail pages
  { path: '/payments/:id/summary', element: <Pages.PaymentSummaryPage /> },
  { path: '/payments/:id', element: <Pages.PaymentDetailsPage /> },
  { path: '/customers/:id', element: <Pages.CustomerDetailsPage /> },
  { path: '/disputes/:id', element: <Pages.DisputeDetailsPage /> },
  { path: '/refunds/:id', element: <Pages.RefundDetailsPage /> },
  { path: '/products/:id', element: <Pages.ProductDetailsPage /> },
  { path: '/ecommerce/:id', element: merchantService('ecommerce', <Pages.EcommerceDetailsPage />) },
  // Inventory and Order details use sheet components on the list page
  { path: '/in-stores/terminals/:id', element: merchantService('pos', <Pages.TerminalDetailsPage />) },
  { path: '/payment-links/:id', element: merchantService('payment-links', <Pages.PaymentLinkDetailsPage />) },
  { path: '/invoices/:id', element: merchantService('billing', <Pages.InvoiceDetailsPage />) },
  { path: '/quotes/:id', element: merchantService('billing', <Pages.QuoteDetailsPage />) },
  { path: '/billing-schedules/:id', element: merchantService('billing', <Pages.BillingScheduleDetailsPage />) },
  { path: '/subscriptions/plans/:id', element: merchantService('billing', <Pages.PlanDetailsPage />) },
  { path: '/products/pricing-tables/:id', element: merchantService('billing', <Pages.PricingTableDetailsPage />) },
  { path: '/subscriptions/:id', element: merchantService('billing', <Pages.SubscriptionDetailsPage />) },
]

/**
 * Protected Full-Page Routes
 * Routes without sidebar (wizards, creation flows, etc.)
 */
export const fullPageRoutes: RouteObject[] = [
  // Payments
  { path: '/payments/create', element: merchantOnly(<Pages.CreatePaymentPage />) },

  // Payment Links
  { path: '/payment-links/create', element: merchantService('payment-links', <Pages.PaymentLinkCreatePage />) },
  { path: '/payment-links/:id/edit', element: merchantService('payment-links', <Pages.PaymentLinkCreatePage />) },

  // Ecommerce
  { path: '/ecommerce/checkout/create', element: merchantService('ecommerce', <Pages.CheckoutConfigCreatePage />) },
  { path: '/ecommerce/checkout/:id/edit', element: merchantService('ecommerce', <Pages.CheckoutConfigCreatePage />) },

  // Sonar Rule Wizard (bank-only)
  { path: '/sonar/rules/new', element: bankOnly(<Pages.RuleCreateWizardPage />) },
  { path: '/sonar/rules/:ruleId/edit', element: bankOnly(<Pages.RuleCreateWizardPage />) },

  // Engagement — Campaign creation wizard
  { path: '/engagement/campaigns/create', element: merchantService('engagement', <Pages.CampaignCreatePage />) },

  // Velocity Wizards — removed (now uses sheet)

  // Compliance Wizards (bank-only, full-page, no sidebar)
  { path: '/compliance/scorecards/new', element: bankOnly(<Pages.ScorecardCreatePage />) },
  { path: '/compliance/scorecards/:id/edit', element: bankOnly(<Pages.ScorecardCreatePage />) },
  { path: '/compliance/sar/new', element: bankOnly(<Pages.SARCaseCreatePage />) },
  { path: '/compliance/konar/forms/new', element: bankOnly(<Pages.FormBuilderPage />) },
  { path: '/compliance/konar/forms/:formId/edit', element: bankOnly(<Pages.FormBuilderPage />) },

  // Fee Schedules — wizard routes removed (now uses sheet), keeping utility pages
  { path: '/billing-admin/fees/:id/versions', element: bankOnly(<Pages.FeeVersionHistoryPage />) },
  { path: '/billing-admin/fees/calculator', element: bankOnly(<Pages.FeeCalculatorPage />) },

  // Tariff Schedules — wizard routes removed (now uses sheet), keeping utility pages
  { path: '/billing-admin/tariffs/:id/versions', element: bankOnly(<Pages.TariffVersionHistoryPage />) },

  // FX Rate Schedules — wizard routes removed (now uses sheet)

  // Reserve Schedules — wizard routes removed (now uses sheet), keeping utility pages
  { path: '/billing-admin/reserves/:id/versions', element: bankOnly(<Pages.ReserveVersionHistoryPage />) },
  { path: '/billing-admin/reserves/calculator', element: bankOnly(<Pages.ReserveImpactCalculatorPage />) },

  // Payout Schedules — wizard routes removed (now uses sheet), keeping utility pages
  { path: '/billing-admin/payouts/:id/versions', element: bankOnly(<Pages.PayoutVersionHistoryPage />) },
  { path: '/billing-admin/payouts/estimator', element: bankOnly(<Pages.PayoutEstimatorPage />) },

  // Bank Wizards (bank-only)
  { path: '/members/banks/create', element: bankOnly(<Pages.BankCreateWizardPage />) },
  { path: '/members/banks/:id/edit', element: bankOnly(<Pages.BankCreateWizardPage />) },

  // Organization Wizards (bank-only)
  { path: '/members/organizations/create', element: bankOnly(<Pages.OrganizationCreateWizardPage />) },
  { path: '/members/organizations/:id/edit', element: bankOnly(<Pages.OrganizationCreateWizardPage />) },

  // Order Wizard
  { path: '/in-stores/orders/new', element: merchantService('pos', <Pages.OrderWizardPage />) },
  { path: '/in-stores/orders/:id/edit', element: merchantService('pos', <Pages.OrderWizardPage />) },

  // Bookings (full-page flows)
  { path: '/bookings/:id/reschedule', element: <Pages.RescheduleBookingFlow /> },
  { path: '/bookings/:id/cancel', element: <Pages.CancelBookingFlow /> },

  // Account Activation
  { path: '/account/activate', element: <Pages.AccountActivationPage /> },
]

/**
 * Public Customer-Facing Routes
 * No authentication required (web ordering, QR ordering, bookings portal)
 */
export const publicRoutes: RouteObject[] = [
  { path: '/order/:storeId', element: <Pages.WebOrderPage /> },
  // QR table ordering
  { path: '/order/:storeId/table/:tableId', element: <Pages.QROrderPage /> },
  // Customer booking portal
  { path: '/book/:storeId', element: <Pages.CustomerBookingPortal /> },
  { path: '/book/:storeId/bookings/:id', element: <Pages.BookingDetailView /> },
  { path: '/book/:storeId/bookings/:id/reminders', element: <Pages.BookingReminders /> },
]
